/*
 * Copyright (c) 2024, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useState} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage} from 'react-intl'
import {
    Box,
    Button,
    Divider,
    Stack,
    Text
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {AbsoluteCenter} from '@chakra-ui/react'
import LoginFields from '@app/components/forms/login-fields'
import StandardLogin from '@app/components/standard-login'
import SocialLogin from '@salesforce/retail-react-app/app/components/social-login'
import {LOGIN_TYPES} from '@app/constants'

const PasswordlessLogin = ({
    form,
    handleForgotPasswordClick,
    handlePasswordlessLoginClick,
    isSocialEnabled = false,
    idps = [],
    prefix = 'login_',
    isFirstInputFocus = false,
    setLoginType
}) => {
    const [showPasswordView, setShowPasswordView] = useState(false)

    const handlePasswordButton = async (e) => {
        setLoginType(LOGIN_TYPES.PASSWORD)
        form.clearErrors('global')
        const isValid = await form.trigger()

        if (!isValid) {
            form.setFocus(`${prefix}email`)
            return
        }

        setShowPasswordView(true)
    }

    return (
        <>
            {((!form.formState.isSubmitSuccessful && !showPasswordView) ||
                form.formState.errors.email) && (
                <Stack spacing={24}>
                    <LoginFields
                        form={form}
                        hidePassword={true}
                        handleForgotPasswordClick={handleForgotPasswordClick}
                        prefix={prefix ? prefix : undefined}
                        isFirstInputFocus={isFirstInputFocus}
                    />
                    <Button
                        type="submit"
                        onClick={() => {
                            handlePasswordlessLoginClick()
                            form.clearErrors('global')
                        }}
                        isLoading={form.formState.isSubmitting}
                        variant="primary"
                    >
                        <FormattedMessage
                            defaultMessage="Continue Securely"
                            id="login_form.button.continue_securely"
                        />
                    </Button>

                    <Box position="relative" px={0} py={6}>
                        <Divider color="borderGray" />
                        <AbsoluteCenter bg="white" px={12}>
                            <Text variant="descriptor" textTransform="uppercase">
                                <FormattedMessage
                                    defaultMessage="Or Login With"
                                    id="login_form.message.or_login_with"
                                />
                            </Text>
                        </AbsoluteCenter>
                    </Box>

                    <Stack spacing={4}>
                        <Button variant="secondary" onClick={handlePasswordButton}>
                            <FormattedMessage
                                defaultMessage="Password"
                                id="login_form.button.password"
                            />
                        </Button>
                        {isSocialEnabled && <SocialLogin form={form} idps={idps} />}
                    </Stack>
                </Stack>
            )}
            {!form.formState.isSubmitSuccessful &&
                showPasswordView &&
                !form.formState.errors.email && (
                    <StandardLogin
                        form={form}
                        handleForgotPasswordClick={handleForgotPasswordClick}
                        setShowPasswordView={setShowPasswordView}
                        hideEmail={true}
                        prefix={prefix ? prefix : undefined}
                    />
                )}
        </>
    )
}

PasswordlessLogin.propTypes = {
    form: PropTypes.object,
    handleForgotPasswordClick: PropTypes.func,
    handlePasswordlessLoginClick: PropTypes.func,
    isSocialEnabled: PropTypes.bool,
    idps: PropTypes.arrayOf(PropTypes.string),
    prefix: PropTypes.string,
    hideEmail: PropTypes.bool,
    isFirstInputFocus: PropTypes.bool,
    setLoginType: PropTypes.func
}

export default PasswordlessLogin
